import React from 'react';
import { Loader2 } from 'lucide-react';
import { useSavingsCalculatorData } from '@/hooks/useSavingsCalculatorData';
import { OnboardingBlocker } from '@/components/onboarding';
import SavingsMetricCard, { SavingsMetricCardProps } from './SavingsMetricCard';
import CardFeesByTypeCard from './CardFeesByTypeCard';
import YearlyBreakdownTable from './YearlyBreakdownTable';
import PaymentMethodsChart from './PaymentMethodsChart';
import AverageFeeCards from './AverageFeeCards';

interface SavingsCalculatorTabProps {
  stripeConnected: boolean;
  onNavigateToTab: (tab: string) => void;
}

const SavingsCalculatorTab: React.FC<SavingsCalculatorTabProps> = ({
  stripeConnected,
  onNavigateToTab,
}) => {
  const {
    totalCardFeesPaid,
    cardTransactionCount,
    cardFeesByType,
    yearlyBreakdown,
    paymentMethodBreakdown,
    averageFeeMetrics,
    feesOnActiveSubscriptions,
    activeCardSubscriptionCount,
    loading,
  } = useSavingsCalculatorData(stripeConnected);

  const cards: SavingsMetricCardProps[] = [
    {
      title: 'Total Stripe Card Fees Paid',
      tooltipText: 'Total estimated Stripe processing fees paid on all your card transactions to date.',
      amount: totalCardFeesPaid,
      periodLabel: 'all time',
      subtitle: `from ${cardTransactionCount.toLocaleString()} card transaction${cardTransactionCount !== 1 ? 's' : ''}`,
      buttonLabel: 'Create Fee Inclusive Checkout Page',
      onButtonClick: () => onNavigateToTab('bundles'),
    },
    {
      title: 'Fees on Active Subscriptions',
      tooltipText: 'Estimated annual Stripe fees being paid on your active card subscriptions. Update subscriptions to eliminate these fees.',
      amount: feesOnActiveSubscriptions,
      periodLabel: 'per year',
      subtitle: `From ${activeCardSubscriptionCount} active card subscription${activeCardSubscriptionCount !== 1 ? 's' : ''}`,
      buttonLabel: 'Update Subscriptions to Cover Fees',
      onButtonClick: () => onNavigateToTab('subscriptions'),
    },
  ];

  return (
    <OnboardingBlocker
      requireStripe={true}
      customMessage="Connect your Stripe account to see your fee savings calculator."
    >
      <div className="container max-w-4xl py-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {loading
            ? cards.map((_, i) => (
                <div
                  key={i}
                  className="border-2 border-red-300 rounded-xl bg-white dark:bg-background p-6 flex items-center justify-center min-h-[180px]"
                >
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ))
            : cards.map((card, i) => <SavingsMetricCard key={i} {...card} />)}
        </div>

        <div className="mt-6">
          <CardFeesByTypeCard data={cardFeesByType} loading={loading} />
        </div>

        <div className="mt-6">
          <YearlyBreakdownTable rows={yearlyBreakdown} loading={loading} />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <PaymentMethodsChart data={paymentMethodBreakdown} loading={loading} />
          <AverageFeeCards data={averageFeeMetrics} loading={loading} />
        </div>
      </div>
    </OnboardingBlocker>
  );
};

export default SavingsCalculatorTab;
